type BudgetAmountModalProps = {
  open: boolean;
  title: string;
  hint: string;
  planPlaceholder: string;
  currentPlaceholder: string;
  cancelLabel: string;
  saveLabel: string;
  budgetValue: string;
  actualValue: string;
  onBudgetChange: (value: string) => void;
  onActualChange: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
  inputAmountOnlyText: string;
  clearAmountLabel: string;
};

export default function BudgetAmountModal({
  open,
  title,
  hint,
  planPlaceholder,
  currentPlaceholder,
  cancelLabel,
  saveLabel,
  budgetValue,
  actualValue,
  onBudgetChange,
  onActualChange,
  onCancel,
  onSave,
  inputAmountOnlyText,
  clearAmountLabel,
}: BudgetAmountModalProps) {
  if (!open) return null;
  const sanitizeAmountInput = (value: string) => {
    let sanitized = "";
    let hasDecimal = false;

    for (const character of value.replace(/\s+/g, "")) {
      if (/\d/.test(character)) {
        sanitized += character;
        continue;
      }

      if (character === "," && !hasDecimal) {
        sanitized += character;
        continue;
      }

      if (character === "." && !hasDecimal) {
        sanitized += character;
        hasDecimal = true;
      }
    }

    return sanitized;
  };
  const amountPattern = /^(?:\d[\d,]*(?:\.\d*)?|\.\d*)$/;
  const invalidPlanAmount =
    budgetValue.trim() !== "" && !amountPattern.test(budgetValue.trim());
  const invalidCurrentAmount =
    actualValue.trim() !== "" && !amountPattern.test(actualValue.trim());
  const hasInvalidAmount = invalidPlanAmount || invalidCurrentAmount;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "var(--modal-backdrop)" }}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-[1.75rem] border border-[color:var(--field-border)] bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_32%),linear-gradient(180deg,rgba(255,251,235,0.98),rgba(255,255,255,0.98))] shadow-[var(--modal-shadow)]">
        <div className="border-b border-[color:var(--field-border)] px-6 py-5">
          <span className="inline-flex rounded-full bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[color:var(--muted)] shadow-sm">
            Totals
          </span>
          <h3 className="mt-3 text-xl font-semibold text-[color:var(--foreground)]">{title}</h3>
          <p className="mt-2 text-sm text-[color:var(--muted)]">{hint}</p>
        </div>
        <div className="grid gap-4 px-6 py-5 sm:grid-cols-2">
          <div className="rounded-[1.5rem] border border-emerald-200/80 bg-white/90 p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-700">
                {planPlaceholder}
              </span>
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">
                Plan
              </span>
            </div>
            <div className="relative">
              <input
                value={budgetValue}
                onChange={(event) => onBudgetChange(sanitizeAmountInput(event.target.value))}
                inputMode="decimal"
                placeholder={planPlaceholder}
                className="w-full rounded-2xl border border-emerald-200 bg-white px-4 py-3 pr-10 text-base font-semibold text-[color:var(--foreground)] outline-none transition placeholder:font-normal focus:border-emerald-400"
              />
              {budgetValue.trim() ? (
                <button
                  type="button"
                  onClick={() => onBudgetChange("")}
                  className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-black transition hover:bg-emerald-100 dark:text-emerald-100"
                  aria-label={clearAmountLabel}
                  title={clearAmountLabel}
                >
                  <span className="text-base font-bold leading-none" aria-hidden="true">
                    ×
                  </span>
                </button>
              ) : null}
            </div>
            {invalidPlanAmount ? <p className="mt-2 text-xs text-red-500">{inputAmountOnlyText}</p> : null}
          </div>
          <div className="rounded-[1.5rem] border border-amber-200/80 bg-white/90 p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                {currentPlaceholder}
              </span>
              <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-700">
                Current
              </span>
            </div>
            <div className="relative">
              <input
                value={actualValue}
                onChange={(event) => onActualChange(sanitizeAmountInput(event.target.value))}
                inputMode="decimal"
                placeholder={currentPlaceholder}
                className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 pr-10 text-base font-semibold text-[color:var(--foreground)] outline-none transition placeholder:font-normal focus:border-amber-400"
              />
              {actualValue.trim() ? (
                <button
                  type="button"
                  onClick={() => onActualChange("")}
                  className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-black transition hover:bg-amber-100 dark:text-amber-100"
                  aria-label={clearAmountLabel}
                  title={clearAmountLabel}
                >
                  <span className="text-base font-bold leading-none" aria-hidden="true">
                    ×
                  </span>
                </button>
              ) : null}
            </div>
            {invalidCurrentAmount ? <p className="mt-2 text-xs text-red-500">{inputAmountOnlyText}</p> : null}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 pb-6">
          <button
            type="button"
            data-tab-trigger="true"
            onClick={onCancel}
            className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-tab-trigger="true"
            onClick={onSave}
            disabled={hasInvalidAmount}
            className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white transition ${
              hasInvalidAmount
                ? "cursor-not-allowed bg-[color:var(--muted)]/60"
                : "bg-[color:var(--accent)] hover:brightness-110"
            }`}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
